/**
 * API client for fetching reviews from baggy-reviews API
 */

const API_BASE_URL = 'https://baggy-reviews.vercel.app/api/reviews';

/**
 * @typedef {Object} ReviewSummary
 * @property {number} averageRating
 * @property {number} totalReviews
 * @property {Object} ratingDistribution
 * @property {number} ratingDistribution.1
 * @property {number} ratingDistribution.2
 * @property {number} ratingDistribution.3
 * @property {number} ratingDistribution.4
 * @property {number} ratingDistribution.5
 */

/**
 * @typedef {Object} PublicReview
 * @property {string} id
 * @property {string} product_sku
 * @property {number} rating
 * @property {string | null} review_text
 * @property {string | null} customer_name
 * @property {boolean} verified_purchase
 * @property {string} created_at
 */

/**
 * @typedef {Object} PublicPaginatedReviews
 * @property {PublicReview[]} reviews
 * @property {Object} pagination
 * @property {number} pagination.page
 * @property {number} pagination.limit
 * @property {number} pagination.total
 * @property {number} pagination.totalPages
 */

/**
 * @typedef {Object} GetReviewsQuery
 * @property {string} productId
 * @property {number} [stars]
 * @property {number} [page]
 * @property {number} [limit]
 * @property {string} [category]
 */

/**
 * Fetches the review summary for a product
 * @param {string} productId - The product ID
 * @returns {Promise<ReviewSummary | null>}
 */
export async function fetchReviewSummary(productId) {
  if (!productId) return null;

  try {
    const url = new URL(`${API_BASE_URL}/summary`);
    url.searchParams.set('productId', productId);

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      if (response.status === 404) {
        // No reviews found for this product
        return null;
      }
      throw new Error(`Failed to fetch review summary: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching review summary:', error);
    return null;
  }
}

/**
 * Fetches a list of reviews for a product
 * @param {GetReviewsQuery} query - The query parameters
 * @returns {Promise<PublicPaginatedReviews | null>}
 */
export async function fetchReviews(query) {
  if (!query?.productId) return null;

  try {
    const url = new URL(API_BASE_URL);
    url.searchParams.set('productId', query.productId);

    if (query.stars) {
      url.searchParams.set('stars', query.stars.toString());
    }
    if (query.page) {
      url.searchParams.set('page', query.page.toString());
    }
    if (query.limit) {
      url.searchParams.set('limit', query.limit.toString());
    }
    if (query.category) {
      url.searchParams.set('category', query.category);
    }

    const response = await fetch(url.toString());
    if (!response.ok) {
      if (response.status === 404) {
        // No reviews found
        return null;
      }
      throw new Error(`Failed to fetch reviews: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return null;
  }
}
